// ListPreview.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Button, Card, Col, Divider, Drawer, Row, Tooltip } from 'antd';
import translateX from 'utils/translateX';
import { BorderlessTableOutlined, FormOutlined, StarOutlined } from '@ant-design/icons';
import DocumentIdentifier from 'components/core/DocumentIdentifier';
import dayjs from 'dayjs';
import React, { useEffect, useRef } from 'react';
import useZustand from 'hooks/useZustand';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************

// *******************************************************************************
// FUNCTIONS
// *******************************************************************************

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const InfoRow = ({ icon, titleKey, value, className }) => (
  <Row className={`preview-row text-truncate ${className}`} wrap={false} align="middle">
    <Tooltip title={translateX(titleKey)} placement="right">
      {icon}
      <span className="ml-3 text-dark text-uppercase">{value}</span>
    </Tooltip>
  </Row>
);

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const ListPreview = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    handleEditDocument,
    handleArchiveDocuments,
    handleRemoveDocuments,
    handleRestoreDocuments,
    selectedDocument,
    updateSelectedDocument,
    show,
    setShow,
    folder,
    refresh,
  } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const storedCurrentPage = useZustand('module', 'currentPage');
  const preview = storedCurrentPage?.preview;
  const { actions = [] } = preview || {};

  // Ref to track which document was fetched to avoid re-fetching
  const lastFetchedIdRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const title = preview?.components?.title || false;
  const showDocumentCard = preview?.showCardHeader || false;
  const noDividerAfterHeader = preview?.noDividerAfterHeader || false;
  const headerComponent = preview?.components?.header || null;
  const bodyComponent = preview?.components?.body || null;
  const actionsComponent = preview?.components?.actions || null;
  const footerComponent = preview?.components?.footer || null;

  const lastUpdateDate =
    selectedDocument?._metadata?.audit?.updatedAt || selectedDocument?._metadata?.audit?.createdAt;
  const shouldRenderOpenButton =
    !actionsComponent && actions.includes('edit') && selectedDocument?._id;

  // Check if document is from homologation environment
  const isHomologation = selectedDocument?.environment === 'homologation';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Enrich selectedDocument with complete data from store when preview opens
  // Table row data may not include all fields (e.g., avatar for QRCode)
  useEffect(() => {
    // Skip if drawer is closed, no document selected, or already enriched this document
    if (!show || !selectedDocument?._id || lastFetchedIdRef.current === selectedDocument._id) {
      return;
    }

    // Get complete data from store
    const storeData = storedCurrentPage?.data || [];
    const completeDocument = storeData.find(doc => doc._id === selectedDocument._id);

    if (completeDocument) {
      // Update the selected document with complete data from store
      updateSelectedDocument(completeDocument);
      // Mark this document as enriched to avoid re-processing
      lastFetchedIdRef.current = selectedDocument._id;
    }
  }, [show, selectedDocument?._id, storedCurrentPage?.data, updateSelectedDocument]);

  // Reset tracker when drawer closes
  useEffect(() => {
    if (!show) {
      lastFetchedIdRef.current = null;
    }
  }, [show]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // const handleOpenButton = () => {
  //   setPageStore({
  //     navigate,
  //     pageName: preview.editGoToPage,
  //     params: { _id: document._id },
  //   });
  //   console.log('goto', document);
  //   // navigate(`${selectedModule.url}/${document._id}`);
  // };

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Drawer
      title={translateX(title || 'preview')}
      size={300}
      placement="left"
      onClose={() => setShow(false)}
      closable={false}
      open={show}
    >
      {/* Homologation Watermark */}
      {isHomologation && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(-45deg)',
            fontSize: 48,
            fontWeight: 'bold',
            color: 'rgba(0, 0, 0, 0.06)',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: 1,
            userSelect: 'none',
          }}
        >
          {translateX('homologation')}
        </div>
      )}

      <Col>
        {/* MODULE PREVIEW HEADER COMPONENT */}
        {headerComponent ? (
          <>
            {headerComponent?.(props)}
            {!noDividerAfterHeader && <Divider dashed className="mt-0" />}
          </>
        ) : null}
        {/* MODULE PREVIEW HEADER COMPONENT */}

        {showDocumentCard ? (
          <Card title={<h6 className="text-muted text-uppercase">{translateX('document')}</h6>}>
            <InfoRow
              icon={<BorderlessTableOutlined />}
              titleKey="document_code"
              value={`${selectedDocument?._metadata?.counter}.${selectedDocument?._metadata?.documentCode}`}
            />

            <InfoRow
              icon={<StarOutlined />}
              className="mt-2 mb-2"
              titleKey="creation_date"
              value={dayjs(selectedDocument?._metadata?.audit?.createdAt).format('L LT')}
            />

            <InfoRow
              icon={<FormOutlined />}
              titleKey="last_update"
              value={dayjs(lastUpdateDate).format('L LT')}
            />

            {/*<Row justify="end" align="middle" className="pt-3" style={{ minHeight: 18 }}>*/}
            {/*  {components?.formHeaderCustomIcons && components.formHeaderCustomIcons(props)}*/}
            {/*  <DocumentTags record={document} className={components?.formHeaderCustomIcons ? 'ml-2' : ''} />*/}
            {/*</Row>*/}
          </Card>
        ) : null}

        {/* MODULE PREVIEW BODY COMPONENT */}
        <div className="mt-3">{bodyComponent && bodyComponent?.(props)}</div>
        {/* MODULE PREVIEW BODY COMPONENT */}
      </Col>

      {shouldRenderOpenButton && (
        <Row justify="center" className="mt-3">
          <Button type="default" style={{ width: '100%' }} onClick={handleEditDocument}>
            {translateX('open')}
          </Button>
        </Row>
      )}

      {/* MODULE PREVIEW ACTIONS COMPONENT */}
      <div className="mt-3">{actionsComponent && actionsComponent?.(props)}</div>
      {/* MODULE PREVIEW ACTIONS COMPONENT */}

      {/* MODULE PREVIEW FOOTER COMPONENT */}
      <div className="mt-3">{footerComponent && footerComponent?.(props)}</div>
      {/* MODULE PREVIEW FOOTER COMPONENT */}

      {/* DRAWER FOOTER */}
      <div>
        <div className="mb-4" />
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            width: '100%',
            padding: '10px 16px',
            left: 0,
            borderRadius: '0 0 4px 4px',
          }}
        >
          <DocumentIdentifier data={selectedDocument} style={{ justifyContent: 'center' }} wrap />
        </div>
      </div>
      {/* DRAWER FOOTER */}
    </Drawer>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default ListPreview;
